// noinspection PointlessArithmeticExpressionJS
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BufferGeometry, DoubleSide, Float32BufferAttribute, MathUtils as ThreeMathUtils, Mesh, MeshBasicMaterial, Object3D, Uint8BufferAttribute, Vector3, } from "three";
import { FileLoader } from "./FileLoader";
import { AltitudeMode, KMLElementType, KMLParserSession, StyleState, } from "../kml";
import { Cartographic, Ellipsoid, MathUtils } from "../../math";
import earcut from "earcut";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2";
import { OptionsManager } from "../../options";
import { LabelCollection } from "../../objects/labelCollection";
import { PickingLineMaterial } from "../../materials";
/**
 * Our implementation of loading OBJ files from remote endpoints.
 */
export class KmlLoader {
    static load(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const text = yield FileLoader.loadText(url, {
                headers: options.headers,
                cacheKey: options.cacheKey,
                cache: true,
            });
            const session = new KMLParserSession();
            const kml = session.parseText(text);
            if (kml === undefined) {
                return undefined;
            }
            const geometry = {
                points: {
                    labels: [],
                    min: new Vector3(Infinity, Infinity, Infinity),
                    max: new Vector3(-Infinity, -Infinity, -Infinity),
                },
                lines: new Map(),
                polys: {
                    positions: [],
                    colours: [],
                    indices: [],
                    min: new Vector3(Infinity, Infinity, Infinity),
                    max: new Vector3(-Infinity, -Infinity, -Infinity),
                },
            };
            const object = new Object3D();
            this.convertContainer(session, geometry, kml);
            this.convertPointGeometryToObject(geometry.points, object, options.glyphCache);
            this.convertPolyGeometryToObject(geometry.polys, object);
            this.convertLinesGeometryToObject(geometry.lines, object);
            return object;
        });
    }
    static convertContainer(session, geometry, container) {
        const stack = [container];
        while (stack.length > 0) {
            const curr = stack.pop();
            for (const child of curr.features) {
                if (child.isContainerElement) {
                    stack.push(child);
                }
                else if (child.isFeatureElement && child.elementType === KMLElementType.Placemark) {
                    this.convertPlacemark(session, geometry, child);
                }
            }
        }
    }
    static convertPlacemark(session, geometry, placemark) {
        // Resolve all style fields, first by loading from the style referred to by ID, then loading each inline style
        // and overlaying it on top of all previous styles
        const style = {
            elementType: KMLElementType.Style,
            isColorStyle: false,
            isContainerElement: false,
            isFeatureElement: false,
            isGeometryElement: false,
            isStyleSelector: true,
        };
        // Follow the style reference chain to overlay the actual referred to style, then overlay all inline styles.
        if (placemark.styleUri !== undefined) {
            this.resolveStyleReferenceChain(session, style, placemark.styleUri);
        }
        for (const selector of placemark.styleSelectors) {
            if (selector.elementType === KMLElementType.Style) {
                const castSelector = selector;
                this.overlayStyle(session, style, castSelector);
            }
        }
        // Construct our placemark state object and loop over all geometry objects to accumulate their geometry into
        // our state object. We batch all child geometry of a placemark into a single set of objects rather than a
        // distinct object per geometry instance.
        const placemarkGeometry = {
            pointName: placemark.name,
            lineWidth: this.selectLineWidth(style),
            fill: this.selectFillEnabled(style),
            outline: this.selectOutlineEnabled(style),
            lineColour: this.selectLineColour(style),
            fillColour: this.selectFillColour(style),
            points: geometry.points,
            lines: geometry.lines,
            polys: geometry.polys,
        };
        for (const child of placemark.geometries) {
            this.convertGeometry(session, placemarkGeometry, child);
        }
    }
    static convertGeometry(session, placemark, geometry) {
        switch (geometry.elementType) {
            case KMLElementType.Point:
                this.convertPoint(session, placemark, geometry);
                break;
            case KMLElementType.LineString:
                this.convertLineStringOrLinearRing(session, placemark, geometry);
                break;
            case KMLElementType.LinearRing:
                this.convertLineStringOrLinearRing(session, placemark, geometry);
                break;
            case KMLElementType.Polygon:
                this.convertPolygon(session, placemark, geometry);
                break;
            case KMLElementType.MultiGeometry:
                this.convertMultiGeometry(session, placemark, geometry);
                break;
            default:
                throw new Error("Element is not a Geometry element");
        }
    }
    static convertPoint(_session, placemark, geometry) {
        var _a;
        if (placemark.pointName !== undefined) {
            const altitudeMode = (_a = geometry.altitudeMode) !== null && _a !== void 0 ? _a : AltitudeMode.ClampToGround;
            const labelPos = new Vector3();
            const label = {
                text: placemark.pointName,
                position: labelPos,
            };
            const scratchC = new Cartographic();
            const height = altitudeMode === AltitudeMode.ClampToGround ? 0 : geometry.coordinates[2];
            const heightOffset = OptionsManager.KML_HEIGHT_OFFSET + OptionsManager.KML_TEXT_HEIGHT_OFFSET;
            scratchC.set(MathUtils.degreesToRadians(geometry.coordinates[0]), MathUtils.degreesToRadians(geometry.coordinates[1]), height + heightOffset);
            Ellipsoid.WGS_84.cartesianForCartographic(scratchC, labelPos);
            // Update the min and max points
            placemark.points.min.min(labelPos);
            placemark.points.max.max(labelPos);
            placemark.points.labels.push(label);
        }
    }
    static convertLineStringOrLinearRing(_session, placemark, object) {
        // TODO: ColorMode?
        var _a;
        const altitudeMode = (_a = object.altitudeMode) !== null && _a !== void 0 ? _a : AltitudeMode.ClampToGround;
        const geometry = this.getOrAddLinesGeometry(placemark, placemark.lineWidth);
        this.addLinearRingGeometry(geometry, altitudeMode, placemark, object.coordinates);
    }
    static convertPolygon(_session, placemark, polygon) {
        if (placemark.fill) {
            this.convertPolygonFill(_session, placemark, polygon);
        }
        if (placemark.outline) {
            this.convertPolygonOutline(_session, placemark, polygon);
        }
    }
    static convertPolygonFill(_session, placemark, polygon) {
        // TODO: No data uses it yet, but extrude flag
        // TODO: Tessellate flag
        // TODO: ColorMode?
        var _a;
        const altitudeMode = (_a = polygon.altitudeMode) !== null && _a !== void 0 ? _a : AltitudeMode.ClampToGround;
        // Flatten the tessellation input
        const data = [polygon.outerRing.coordinates];
        for (const ring of polygon.innerRings) {
            data.push(ring.coordinates);
        }
        const flattened = earcut.flatten(data);
        // Perform the tessellation
        const indices = earcut(flattened.vertices, flattened.holes, flattened.dimensions);
        const numPositions = flattened.vertices.length / 3;
        const scratchC = new Cartographic();
        const scratchV = new Vector3();
        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const colours = new Array(numPositions * 4);
        for (let i = 0; i < numPositions; i += 1) {
            const height = altitudeMode === AltitudeMode.ClampToGround ? 0 : flattened.vertices[i * 3 + 2];
            scratchC.set(MathUtils.degreesToRadians(flattened.vertices[i * 3 + 0]), MathUtils.degreesToRadians(flattened.vertices[i * 3 + 1]), height + OptionsManager.KML_HEIGHT_OFFSET);
            Ellipsoid.WGS_84.cartesianForCartographic(scratchC, scratchV);
            min.min(scratchV);
            max.max(scratchV);
            flattened.vertices[i * 3 + 0] = scratchV.x;
            flattened.vertices[i * 3 + 1] = scratchV.y;
            flattened.vertices[i * 3 + 2] = scratchV.z;
            colours[i * 4 + 0] = placemark.fillColour[0];
            colours[i * 4 + 1] = placemark.fillColour[1];
            colours[i * 4 + 2] = placemark.fillColour[2];
            colours[i * 4 + 3] = placemark.fillColour[3];
        }
        // Update the min and max values for polygons
        placemark.polys.min.min(min);
        placemark.polys.max.max(max);
        const indexBase = placemark.polys.positions.length / 3;
        placemark.polys.positions.push(...flattened.vertices);
        placemark.polys.colours.push(...colours);
        placemark.polys.indices.push(...indices.map((v) => v + indexBase));
    }
    static convertPolygonOutline(_session, placemark, polygon) {
        // TODO: ColorMode?
        var _a;
        const altitudeMode = (_a = polygon.altitudeMode) !== null && _a !== void 0 ? _a : AltitudeMode.ClampToGround;
        const geometry = this.getOrAddLinesGeometry(placemark, placemark.lineWidth);
        this.addLinearRingGeometry(geometry, altitudeMode, placemark, polygon.outerRing.coordinates);
        for (const ring of polygon.innerRings) {
            this.addLinearRingGeometry(geometry, altitudeMode, placemark, ring.coordinates);
        }
    }
    static addLinearRingGeometry(geometry, altitudeMode, placemark, coordinates) {
        // TODO: ColorMode?
        const [positions, colours, min, max] = this.readLineStringLike(altitudeMode, coordinates, placemark.lineColour);
        const length = positions.length - 3;
        const positionsSegments = new Array(2 * length);
        const coloursSegments = new Array(2 * length);
        for (let i = 0; i < length; i += 3) {
            positionsSegments[2 * i + 0] = positions[i + 0];
            positionsSegments[2 * i + 1] = positions[i + 1];
            positionsSegments[2 * i + 2] = positions[i + 2];
            positionsSegments[2 * i + 3] = positions[i + 3];
            positionsSegments[2 * i + 4] = positions[i + 4];
            positionsSegments[2 * i + 5] = positions[i + 5];
            coloursSegments[2 * i + 0] = colours[i + 0];
            coloursSegments[2 * i + 1] = colours[i + 1];
            coloursSegments[2 * i + 2] = colours[i + 2];
            coloursSegments[2 * i + 3] = colours[i + 3];
            coloursSegments[2 * i + 4] = colours[i + 4];
            coloursSegments[2 * i + 5] = colours[i + 5];
        }
        geometry.min.min(min);
        geometry.max.max(max);
        // For future Keith: If we encounter a call stack overflow again, when there's no recursive call,
        // it's probably because we ran out of stack memory
        for (const position of positionsSegments) {
            geometry.positions.push(position);
        }
        for (const colour of coloursSegments) {
            geometry.colours.push(colour);
        }
    }
    static convertMultiGeometry(session, placemark, geometry) {
        for (const child of geometry.geometries) {
            this.convertGeometry(session, placemark, child);
        }
    }
    static selectLineWidth(style) {
        if (style.lineStyle !== undefined) {
            return style.lineStyle.width;
        }
        else {
            return 1;
        }
    }
    static selectOutlineEnabled(style) {
        if (style.polyStyle !== undefined) {
            return style.polyStyle.outline;
        }
        else {
            return true;
        }
    }
    static selectFillEnabled(style) {
        if (style.polyStyle !== undefined) {
            return style.polyStyle.fill;
        }
        else {
            return true;
        }
    }
    static selectLineColour(style) {
        if (style.lineStyle !== undefined) {
            return style.lineStyle.color;
        }
        else {
            return [1, 1, 1, 1];
        }
    }
    static selectFillColour(style) {
        if (style.polyStyle !== undefined) {
            return style.polyStyle.color;
        }
        else {
            return [1, 1, 1, 1];
        }
    }
    static readLineStringLike(altitudeMode, coordinates, lineColour) {
        // These will hold the min and max coordinates respectively, which will be selected while iterating the
        // coordinates.
        //
        // This is used for calculating the center point of the vertex positions so we can make them relative to center
        // to work around single precision floating point problems
        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        // Scratch variables used for converting the points positions from cartographic to cartesian
        const scratchC = new Cartographic();
        const scratchV = new Vector3();
        // These buffers are where the output vertex positions and vertex colours will be written
        const positions = new Array(coordinates.length * 3);
        const colours = new Array(coordinates.length * 3);
        for (let i = 0; i < coordinates.length; i += 1) {
            // Convert the input position for lat/long/height to cartesian
            const height = altitudeMode === AltitudeMode.ClampToGround ? 0 : coordinates[i][2];
            scratchC.set(MathUtils.degreesToRadians(coordinates[i][0]), MathUtils.degreesToRadians(coordinates[i][1]), height + OptionsManager.KML_HEIGHT_OFFSET);
            Ellipsoid.WGS_84.cartesianForCartographic(scratchC, scratchV);
            // Update the min and max points
            min.min(scratchV);
            max.max(scratchV);
            // Write out the vertex positions
            positions[i * 3 + 0] = scratchV.x;
            positions[i * 3 + 1] = scratchV.y;
            positions[i * 3 + 2] = scratchV.z;
            // Write out our vertex colours
            colours[i * 3 + 0] = lineColour[0];
            colours[i * 3 + 1] = lineColour[1];
            colours[i * 3 + 2] = lineColour[2];
            // colours[i * 4 + 3] = lineColour[3];
        }
        return [positions, colours, min, max];
    }
    static applyCenterOffset(positions, center) {
        for (let i = 0; i < positions.length; i += 3) {
            positions[i + 0] = positions[i + 0] - center.x;
            positions[i + 1] = positions[i + 1] - center.y;
            positions[i + 2] = positions[i + 2] - center.z;
        }
    }
    static convertPointGeometryToObject(pointsGeometry, object, glyphCache) {
        if (pointsGeometry.labels.length > 0) {
            const min = pointsGeometry.min;
            const max = pointsGeometry.max;
            const center = min.clone().add(max).divideScalar(2);
            for (const label of pointsGeometry.labels) {
                label.position.x -= center.x;
                label.position.y -= center.y;
                label.position.z -= center.z;
            }
            const labelCollection = new LabelCollection(glyphCache);
            labelCollection.position.copy(center);
            labelCollection.labels.push(...pointsGeometry.labels);
            object.add(labelCollection);
        }
    }
    static convertPolyGeometryToObject(polygonGeometry, object) {
        // If no polygon or line geometry is present then early exit. The code after this point
        // will break when no geometry is present
        if (polygonGeometry.positions.length === 0) {
            return;
        }
        const min = polygonGeometry.min;
        const max = polygonGeometry.max;
        const center = min.clone().add(max).divideScalar(2);
        this.applyCenterOffset(polygonGeometry.positions, center);
        const geometry = new BufferGeometry();
        geometry.setAttribute("position", new Float32BufferAttribute(polygonGeometry.positions, 3, false));
        geometry.setAttribute("color", new Uint8BufferAttribute(polygonGeometry.colours, 4, true));
        geometry.setIndex(polygonGeometry.indices);
        const polys = new Mesh(geometry, this.MESHMAT);
        polys.position.copy(center);
        object.add(polys);
    }
    static convertLinesGeometryToObject(linesGeometryMap, object) {
        linesGeometryMap.forEach((linesGeometry, _) => {
            // If no polygon or line geometry is present then early exit. The code after this point
            // will break when no geometry is present
            if (linesGeometry.positions.length === 0) {
                return;
            }
            const min = linesGeometry.min;
            const max = linesGeometry.max;
            const center = min.clone().add(max).divideScalar(2);
            this.applyCenterOffset(linesGeometry.positions, center);
            // Construct the geometry objects for our rendering items
            const geometry = new LineSegmentsGeometry();
            geometry.setPositions(linesGeometry.positions);
            geometry.setColors(linesGeometry.colours);
            // Create our rendering objects and add them as children to the placemark object
            const lines = new LineSegments2(geometry, linesGeometry.material);
            lines.computeLineDistances();
            // lines.scale.set(1, 1, 1);
            lines.position.copy(center);
            object.add(lines);
        });
    }
    static getOrAddLinesGeometry(geometry, width) {
        const get = geometry.lines.get(width);
        if (get === undefined) {
            const lines = {
                material: new PickingLineMaterial({
                    vertexColors: true,
                    linewidth: ThreeMathUtils.clamp(width / 2, 1, Infinity),
                    worldUnits: false,
                }),
                positions: [],
                colours: [],
                min: new Vector3(Infinity, Infinity, Infinity),
                max: new Vector3(-Infinity, -Infinity, -Infinity),
            };
            geometry.lines.set(width, lines);
            return lines;
        }
        else {
            return get;
        }
    }
    static resolveStyleReferenceChain(session, style, id) {
        if (id.startsWith("#")) {
            const split = id.split("#");
            const splitId = split[1];
            const referredStyle = session.objectsById.get(splitId);
            if (referredStyle !== undefined) {
                if (referredStyle.elementType === KMLElementType.Style) {
                    const castStyle = referredStyle;
                    this.overlayStyle(session, style, castStyle);
                }
                else if (referredStyle.elementType === KMLElementType.StyleMap) {
                    const castStyle = referredStyle;
                    this.overlayStyleMap(session, style, castStyle);
                }
            }
        }
    }
    static overlayStyle(_session, style, overlay) {
        style.iconStyle = overlay.iconStyle;
        style.labelStyle = overlay.labelStyle;
        style.lineStyle = overlay.lineStyle;
        style.polyStyle = overlay.polyStyle;
        style.balloonStyle = overlay.balloonStyle;
        style.listStyle = overlay.listStyle;
    }
    static overlayStyleMap(session, style, map) {
        const normalStyle = map.map.get(StyleState.Normal);
        if (typeof normalStyle === "string") {
            this.resolveStyleReferenceChain(session, style, normalStyle);
        }
        else {
            this.overlayStyle(session, style, normalStyle);
        }
    }
    static makeLabelCanvas(size, name, scale = 1) {
        const borderSize = 2;
        const ctx = document.createElement("canvas").getContext("2d");
        if (ctx === null)
            throw new Error("No 2D canvas");
        const font = `${size}px bold sans-serif`;
        ctx.font = font;
        // measure how long the name will be
        const textWidth = ctx.measureText(name).width;
        const doubleBorderSize = borderSize * 2 * scale;
        const width = (textWidth + doubleBorderSize) * scale;
        const height = (size + doubleBorderSize) * scale;
        ctx.canvas.width = width;
        ctx.canvas.height = height;
        // need to set font again after resizing canvas
        ctx.font = font;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        ctx.fillStyle = "transparent";
        ctx.fillRect(0, 0, width, height);
        // scale to fit but don't stretch
        ctx.translate(width / 2, height / 2);
        ctx.scale(scale, scale);
        ctx.fillStyle = "white";
        ctx.fillText(name, 0, 0);
        return ctx.canvas;
    }
}
// TODO: We need multiple instances, transparency per-kml requires a unique instance for each kml object
KmlLoader.MESHMAT = new MeshBasicMaterial({
    vertexColors: true,
    side: DoubleSide,
});
